import React, { useEffect, useRef, useState } from 'react';
// NOTE: `useCallback` was removed from the import when the rotating headline
// was disabled below. Re-add it to the import above when you re-enable the
// rotating quotes (nextQuote uses it).
import './Hero.css';

// Pexels: photo/15228261 — calm interior, ZZ plant, neutral palette.
// Swap by replacing this constant. Other candidates live in /assets/hero-slides/ and /our-space/.
const HERO_BG = '/assets/hero/hero-bg.webp';
const HERO_VIDEO = '/assets/hero/hero-slides/hero-video.mp4';
const WHATSAPP_URL = 'https://wa.me/919140395816?text=Hi%2C%20I%27d%20like%20to%20book%20a%20first%20session%20at%20Mind%20Spa.';

// ── ROTATING HEADLINE (disabled) ──────────────────────────────────────────
// The hero used to auto-cycle these quotes as the <h1> every 5s. It's now a
// single static headline (see the JSX below). To restore the rotation:
//   1. re-add `useCallback` to the React import at the top,
//   2. un-comment this HERO_QUOTES array,
//   3. un-comment the currentQuote/slideDir state + nextQuote + interval,
//   4. swap the static <h1> back for the commented quote-dots/quote-wrapper JSX.
/*
const HERO_QUOTES = [
  'Self care is how you take your power back.',
  'Your mental health is a long term investment, not an expense.',
  "It's ok to ask for help, you don't have to figure everything out alone.",
  'Every step you take towards your healing matters.',
  'A positive mind finds opportunity in every challenge.',
  'Focus on progress, not perfection.',
  'Each day is a fresh start — breathe, believe, begin again.',
  'There is strength in allowing yourself to feel.',
];
*/

const Hero = () => {
  const [loaded, setLoaded] = useState(false);
  const [muted, setMuted] = useState(true);
  // Rotating-headline state (disabled — see HERO_QUOTES note above):
  // const [currentQuote, setCurrentQuote] = useState(0);
  // const [slideDir, setSlideDir] = useState('in');
  const videoRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 200);
    return () => clearTimeout(timer);
  }, []);

  // Rotating-headline driver (disabled — see HERO_QUOTES note above):
  /*
  const nextQuote = useCallback(() => {
    setSlideDir('out');
    setTimeout(() => {
      setCurrentQuote((prev) => (prev + 1) % HERO_QUOTES.length);
      setSlideDir('in');
    }, 500);
  }, []);

  useEffect(() => {
    if (!loaded) return undefined;
    const interval = setInterval(nextQuote, 5000);
    return () => clearInterval(interval);
  }, [loaded, nextQuote]);
  */

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    setMuted(next);
    if (!next && v.paused) {
      v.play().catch(() => {});
    }
  };

  return (
    <section id="home" className={`hero ${loaded ? 'hero--loaded' : ''}`}>
      <div className="hero__bg">
        <img
          src={HERO_BG}
          alt="Calm therapy space at Mind Spa India, Lucknow"
          className="hero__bg-img"
          width="1920"
          height="1080"
          loading="eager"
          fetchpriority="high"
          decoding="async"
        />
      </div>
      <div className="hero__overlay" />

      <div className="container hero__container">
        <div className="hero__grid">
          {/* LEFT — copy & CTAs */}
          <div className="hero__content">
            <p className="hero__label">Clinical Hypnotherapy &middot; Lucknow</p>

            {/* Rotating quote dots + auto-changing headline (disabled — see
                the HERO_QUOTES note near the top to restore). Uncomment this
                block and remove the static <h1> below to bring it back.
            <div className="hero__quote-dots">
              {HERO_QUOTES.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`hero__quote-dot ${i === currentQuote ? 'active' : ''}`}
                  onClick={() => {
                    if (i === currentQuote) return;
                    setSlideDir('out');
                    setTimeout(() => {
                      setCurrentQuote(i);
                      setSlideDir('in');
                    }, 500);
                  }}
                  aria-label={`Show quote ${i + 1}`}
                />
              ))}
            </div>

            <div className="hero__quote-wrapper">
              <h1
                key={currentQuote}
                className={`hero__title hero__title--${slideDir}`}
              >
                {HERO_QUOTES[currentQuote]}
              </h1>
            </div>
            */}

            {/* Static headline */}
            <h1 className="hero__title hero__title--static">
              Heal the mind, awaken the spirit
            </h1>

            <p className="hero__subtitle">
              Hypnotherapy training, Psychotherapy, Hypnotherapy and coaching sessions with
              Prof (Dr) Manju Agrawal. EKAA and CHI-USA certified. In Lucknow or online.
            </p>

            <div className="hero__actions">
              <a
                href="#team"
                className="btn-primary hero__btn"
                onClick={(e) => {
                  e.preventDefault();
                  const team = document.getElementById('team');
                  if (team) team.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Book a First Session
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hero__whatsapp-btn"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                <span>Chat on WhatsApp</span>
              </a>
            </div>
          </div>

          {/* RIGHT — inline auto-playing video */}
          <div className="hero__video-wrap">
            <div className="hero__video-frame">
              <video
                ref={videoRef}
                src={HERO_VIDEO}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                className="hero__video"
                aria-label="Mind Spa intro video"
              />
              <button
                type="button"
                className="hero__video-mute"
                onClick={toggleMute}
                aria-label={muted ? 'Unmute video' : 'Mute video'}
                aria-pressed={!muted}
              >
                {muted ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor"/>
                    <line x1="23" y1="9" x2="17" y2="15"/>
                    <line x1="17" y1="9" x2="23" y2="15"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor"/>
                    <path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
